import { message } from 'antd';
import { isAxiosError } from 'axios';
import { debounce, put, type Effect, type ForkEffect, takeEvery, select } from 'redux-saga/effects';
import * as Effects from 'redux-saga/effects';
import { replace } from 'redux-first-history';
import type { PayloadAction } from '@reduxjs/toolkit';

import * as Api from '~/api';
import { getWithParamString } from '~/helpers';

import { Columns } from '~/components/EmailTemplates/Columns';

import type { EmailTemplate } from '~/types/models';
import { type ErrorResponse, type IndexResponse, isErrorResponse } from '~/types/responses';
import type { IRestIndexParams } from '~/types/states';

import { actions as app } from '~/redux/app/slice';
import { watchHandleError } from '~/redux/app/saga';

import { actions } from './slice';
import { getItem, getParams } from './selectors';

const call: any = Effects.call;

export function* watchCreate({ payload: item }: PayloadAction<Partial<EmailTemplate>>): Generator<Effect, void, EmailTemplate> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    yield put(app.title('Création...'));

    const newItem = yield call(Api.create, '/email_templates', item);
    message.success('Gabarit de courriels créé avec succès.');

    yield call(Api.clearCache, /.*email_templates.*/);

    yield put(replace(`/configuration/email_templates/${newItem.humanId}/edit`));
  } catch (e) {
    yield put(app.title('Nouvelle étiquette'));
    yield put(actions.setLoading(false));

    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  }
}

export function* watchDestroy({ payload: { id } }: PayloadAction<EmailTemplate>): Generator<Effect, void, void> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));
  try {
    yield call(Api.destroy, '/email_templates', id);
    message.success('Gabarit de courriels supprimé avec succès.');

    yield call(watchIndex, {});
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchIndex({
  payload: params,
}: PayloadAction<IRestIndexParams<EmailTemplate>>): Generator<Effect, void, IndexResponse<EmailTemplate>> {
  yield put(actions.setLoading(true));
  yield put(actions.setError(null));

  try {
    const currentParams = yield select(getParams);
    const { currentPage, items, pageSize, total, totalPages } = yield call(Api.index, '/email_templates', {
      ...currentParams,
      ...params,
    });
    yield put(actions.setPage(currentPage));
    yield put(actions.setData(items));
    yield put(actions.setPageSize(pageSize));
    yield put(actions.setTotal(total));
    yield put(actions.setTotalPages(totalPages));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchRetrieve({
  payload: { id, params },
}: PayloadAction<{ id: EmailTemplate['id'] | EmailTemplate['humanId']; params?: IRestIndexParams<EmailTemplate> }>): Generator<Effect, void, EmailTemplate> {
  try {
    yield put(actions.setLoading(true));
    yield put(actions.setError(null));
    let item = yield select(getItem);
    if (item?.humanId !== id) {
      yield put(app.title('Chargement...'));
      yield put(actions.setItem(null));
      yield put(actions.setInitialItem(null));

      item = yield call(Api.retrieve, `/email_templates/${id}`, {
        ...(params ?? {}),
        with: getWithParamString(Columns, 'single'),
      });
      yield put(actions.setItem(item));
      yield put(actions.setInitialItem(JSON.stringify(item)));
    }
    yield put(app.title(item.name));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchSetParams({
  payload: params,
}: PayloadAction<IRestIndexParams<EmailTemplate>>): Generator<Effect, void, EmailTemplate> {
  const { sortBy, page, pageSize, ...filters } = params;

  yield put(actions.setPage(page));
  yield put(actions.setPageSize(pageSize));
  yield put(actions.setSortBy(sortBy));
  yield put(actions.setFilters(filters));
}

export function* watchUpdate({ payload: item }: PayloadAction<EmailTemplate>): Generator<Effect, void, EmailTemplate> {
  if (!item.id) {
    return;
  }

  yield put(actions.setLoading(true));
  yield put(app.title('Sauvegarde...'));
  yield put(actions.setError(null));

  try {
    yield call(Api.update, `/email_templates/${item.id}`, item);
    const updatedItem = yield call(Api.retrieve, `/email_templates/${item.id}`, {
      with: getWithParamString(Columns, 'single'),
    });
    yield put(actions.setItem(updatedItem));
    yield put(actions.setInitialItem(JSON.stringify(updatedItem)));
    message.success('Gabarit de courriels mis à jour avec succès.');

    yield put(app.title(updatedItem.name));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, Columns);
    }
  } finally {
    yield put(app.title(item.name));
    yield put(actions.setLoading(false));
  }
}

export function* watchEmailTemplatesSagas(): Generator<ForkEffect, void> {
  yield debounce(500, actions.index, watchIndex);
  yield debounce(100, actions.retrieve, watchRetrieve);
  yield takeEvery(actions.create, watchCreate);
  yield takeEvery(actions.destroy, watchDestroy);
  yield takeEvery(actions.setParams, watchSetParams);
  yield takeEvery(actions.update, watchUpdate);
}

const emailTemplatesSagas = watchEmailTemplatesSagas;

export default emailTemplatesSagas;
